import React from 'react';

/*
 * Grafik untuk dashboard admin — SVG mentah, nol dependensi.
 *
 * Dibuat sendiri karena bentuknya cuma dua: satu area dengan sumbu, dan satu
 * sparkline tanpa sumbu. Pustaka grafik apa pun berukuran puluhan kali lipat
 * dari kode ini dan tetap harus di-override supaya cocok dengan tokennya.
 *
 * Semua warna lewat var(--...) supaya ikut berganti saat tema diganti tanpa
 * merender ulang apa pun.
 */

const path = (pts, close) => {
  if (!pts.length) return '';
  const d = 'M' + pts.map(p => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('L');
  return close ? `${d}L${pts[pts.length - 1][0].toFixed(1)} ${close}L${pts[0][0].toFixed(1)} ${close}Z` : d;
};

/*
 * Grafik area untuk kartu Revenue.
 *
 * Sumbu Y dimulai dari nol, selalu. Memulai dari nilai terendah membuat selisih
 * kecil terlihat dramatis — pada data setipis ini satu pekerjaan AED 44 akan
 * tampak seperti lonjakan besar.
 */
export class AreaChart extends React.Component {
  state = { hover: null };
  render() {
    const {
      series, valueKey = 'gross', height = 190, prefix = '', locale = 'en-US', summary
    } = this.props;
    const { hover } = this.state;
    const W = 720, H = height, PAD_L = 44, PAD_B = 22, PAD_T = 10;
    const vals = series.map(s => Number(s[valueKey]) || 0);
    const max = Math.max.apply(null, vals.concat([1]));
    const plotW = W - PAD_L;
    const stepX = series.length > 1 ? plotW / (series.length - 1) : plotW;
    const y = v => PAD_T + (H - PAD_T - PAD_B) * (1 - v / max);
    const pts = vals.map((v, i) => [PAD_L + i * stepX, y(v)]);

    // Empat garis bantu, termasuk nol, mengikuti chart desain client.
    const lines = [0, 1 / 3, 2 / 3, 1].map(f => ({ f, v: max * f }));
    const number = (v, options) => Number(v || 0)
      .toLocaleString(locale === 'ar' ? 'ar-AE' : 'en-GB', options);
    const axis = v => {
      if (v >= 1000000) return `${number(v / 1000000, { maximumFractionDigits: 1 })}${locale === 'ar' ? 'م' : 'M'}`;
      if (v >= 1000) return `${number(v / 1000, { maximumFractionDigits: 1 })}${locale === 'ar' ? 'ألف' : 'k'}`;
      return number(Math.round(v));
    };

  // Label sumbu X: hanya ujung dan tengah. Tiga puluh tanggal berjejer tidak
  // terbaca dan tidak menambah informasi apa pun.
    const ticks = series.length
      ? [0, Math.floor(series.length / 2), series.length - 1]
        .filter((v, i, a) => a.indexOf(v) === i)
      : [];
    const short = l => (l.length === 10 ? l.slice(5) : l);

    return (
      <div className="chart" dir="ltr" style={{ position: 'relative' }}
        onMouseLeave={() => this.setState({ hover: null })}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height }}>
          {lines.map(l => (
            <g key={l.f}>
              <text className="axis-t" x={PAD_L - 8} y={y(l.v) + 4} textAnchor="end">
                {axis(l.v)}
              </text>
              <line className="gridline" x1={PAD_L} x2={W} y1={y(l.v)} y2={y(l.v)} />
            </g>
          ))}
          <path d={path(pts, H - PAD_B)} fill="url(#areaFill)" />
          <path d={path(pts)} fill="none" stroke="var(--brand-amber)" strokeWidth="2"
            strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />

          {/* Titik hover. Kotak sasarannya selebar satu langkah dan setinggi
              grafik — mengejar garis setebal 2 px dengan kursor tidak mungkin. */}
          {pts.map((p, i) => (
            <g key={i}>
              <rect className="dot-hit" x={p[0] - stepX / 2} y="0" width={stepX} height={H}
                onMouseEnter={() => this.setState({ hover: i })} />
              <circle className={`dot-v${hover === i ? ' on' : ''}`} cx={p[0]} cy={p[1]} r="4"
                vectorEffect="non-scaling-stroke" />
            </g>
          ))}

          <defs>
            <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--brand-amber)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="var(--brand-amber)" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {hover !== null && (
          <div className="tip on" style={{
            insetInlineStart: `${(pts[hover][0] / W) * 100}%`,
            top: `${(pts[hover][1] / H) * 100}%`
          }}>
            <b>{prefix}{number(Math.round(vals[hover]))}</b>
            <span style={{ display: 'block', opacity: .7 }}>{series[hover].label}</span>
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: 10.5, color: 'var(--tx-3)', marginTop: 2,
          marginInlineStart: `${(PAD_L / W) * 100}%`
        }}>
          {ticks.map(i => <span key={i}>{short(series[i].label)}</span>)}
        </div>
        <span className="sr">
          {summary
            ? summary(`${prefix}${number(Math.round(Math.max.apply(null, vals)))}`, number(series.length))
            : `${prefix}${number(Math.round(Math.max.apply(null, vals)))} highest across ${number(series.length)} points`}
        </span>
      </div>
    );
  }
}

/*
 * Sparkline ubin. Tanpa sumbu, tanpa label — bentuk saja.
 *
 * Deret yang seluruhnya nol digambar sebagai garis datar di dasar, bukan
 * disembunyikan: ubin tanpa grafik di antara ubin bergrafik terlihat seperti
 * gagal memuat, sedangkan garis datar memang berarti tidak ada apa-apa.
 */
export const Spark = ({ series, valueKey, height = 28 }) => {
  const W = 200, H = height;
  const vals = series.map(s => Number(s[valueKey]) || 0);
  const max = Math.max.apply(null, vals.concat([1]));
  const stepX = vals.length > 1 ? W / (vals.length - 1) : W;
  const pts = vals.map((v, i) => [i * stepX, H - 2 - (H - 4) * (v / max)]);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={path(pts, H)} fill="var(--brand-amber)" opacity="0.14" />
      <path d={path(pts)} fill="none" stroke="var(--brand-amber)" strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};
